import { useState, useEffect } from 'react';
import { fetchAdminUserById, updateAdminUser } from '../../services/adminApi';

const TYPE_LABELS = {
    company: 'Spoločnosť',
    school: 'Škola',
    town: 'Obec',
    transporter: 'Prepravca',
    'recycling center': 'Zberný dvor',
};

const ROLE_META = {
    admin: { label: 'Admin',  color: 'purple' },
    user:  { label: 'User',   color: 'gray'   },
    demo:  { label: 'Demo',   color: 'yellow' },
};

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
};

function Row({ label, value, mono }) {
    return (
        <div className="adm-detail-row">
            <span className="adm-detail-label">{label}</span>
            <span className={`adm-detail-value${mono ? ' adm-td-mono' : ''}`}>{value ?? '—'}</span>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="adm-detail-section">
            <div className="adm-detail-section-title">{title}</div>
            {children}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="adm-detail-label">{label}</span>
            {children}
        </div>
    );
}

function EditForm({ user, onSave, onCancel }) {
    const [name, setName] = useState(user.name ?? '');
    const [surname, setSurname] = useState(user.surname ?? '');
    const [email, setEmail] = useState(user.email ?? '');
    const [username, setUsername] = useState(user.username ?? '');
    const [contact, setContact] = useState(user.contact ?? '');
    const [role, setRole] = useState(user.role);
    const [blocked, setBlocked] = useState(user.blocked ?? false);
    const [verified, setVerified] = useState(user.verified ?? false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const handleSave = async () => {
        setError(null);
        setSaving(true);
        try {
            await onSave({ name, surname, email, username, contact, role, blocked, verified });
        } catch (err) {
            setError(err.response?.data?.message ?? 'Uloženie zlyhalo.');
        } finally {
            setSaving(false);
        }
    };

    const inputStyle = { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--adm-border)', background: 'var(--adm-bg-2)', color: 'var(--adm-text)', fontSize: 13 };

    return (
        <div className="adm-detail-section">
            <div className="adm-detail-section-title">Úprava</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Field label="Meno">
                        <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} />
                    </Field>
                    <Field label="Priezvisko">
                        <input style={inputStyle} value={surname} onChange={e => setSurname(e.target.value)} />
                    </Field>
                </div>

                <Field label="E-mail">
                    <input style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)} />
                </Field>

                <Field label="Username">
                    <input style={inputStyle} value={username} onChange={e => setUsername(e.target.value)} />
                </Field>

                <Field label="Telefón">
                    <input style={inputStyle} value={contact} onChange={e => setContact(e.target.value)} placeholder="+421xxxxxxxxx" />
                </Field>

                <Field label="Rola">
                    <select className="adm-filter-select" style={{ width: '100%' }} value={role} onChange={e => setRole(e.target.value)}>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="demo">Demo</option>
                    </select>
                </Field>

                <div style={{ display: 'flex', gap: 16 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                        <input type="checkbox" checked={verified} onChange={e => setVerified(e.target.checked)} />
                        Verifikovaný
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                        <input type="checkbox" checked={blocked} onChange={e => setBlocked(e.target.checked)} />
                        Blokovaný
                    </label>
                </div>
            </div>

            {error && <p className="adm-extend-error" style={{ marginTop: 8 }}>{error}</p>}

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="adm-extend-btn" onClick={handleSave} disabled={saving}>
                    {saving ? <span className="adm-btn-spinner" /> : 'Uložiť'}
                </button>
                <button
                    className="adm-extend-btn"
                    style={{ background: 'var(--adm-bg-2)', color: 'var(--adm-text)' }}
                    onClick={onCancel}
                    disabled={saving}
                >
                    Zrušiť
                </button>
            </div>
        </div>
    );
}

export default function UserDetail({ userId, onClose, onUpdate, onTenantClick }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (!userId) return;
        setLoading(true);
        setError(null);
        setEditing(false);
        fetchAdminUserById(userId)
            .then(res => setUser(res.data))
            .catch(() => setError('Nepodarilo sa načítať detail používateľa.'))
            .finally(() => setLoading(false));
    }, [userId]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const handleSave = async (payload) => {
        const res = await updateAdminUser(userId, payload);
        setUser(res.data);
        setEditing(false);
        onUpdate?.();
    };

    const role = user ? (ROLE_META[user.role] ?? { label: user.role, color: 'gray' }) : null;

    return (
        <>
            <div className="adm-drawer-backdrop" onClick={onClose} />
            <aside className="adm-drawer">
                <div className="adm-drawer-header">
                    <div>
                        {loading || !user ? (
                            <div className="adm-drawer-title">Načítavam...</div>
                        ) : (
                            <>
                                <div className="adm-drawer-title">{user.name} {user.surname}</div>
                                <div className="adm-drawer-subtitle">{user.email}</div>
                            </>
                        )}
                    </div>
                    <button className="adm-drawer-close" onClick={onClose} aria-label="Zavrieť">✕</button>
                </div>

                <div className="adm-drawer-main">
                    {loading && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0', width: '100%' }}>
                            <span className="adm-spinner" />
                        </div>
                    )}

                    {error && <div className="adm-error" style={{ margin: 24 }}>{error}</div>}

                    {!loading && user && (
                        <>
                            <aside className="adm-drawer-sidebar">
                                <Section title="Osobné údaje">
                                    <Row label="Meno" value={`${user.name} ${user.surname}`} />
                                    <Row label="E-mail" value={user.email} />
                                    <Row label="Používateľské meno" value={user.username} mono />
                                    <Row label="Telefón" value={user.contact} />
                                </Section>

                                <Section title="Účet">
                                    <Row label="Rola" value={
                                        <span className={`adm-badge adm-badge--${role.color}`}>{role.label}</span>
                                    } />
                                    <Row label="Registrácia" value={formatDate(user.registrationDate)} />
                                    <Row label="Verifikovaný" value={
                                        <span className={`adm-badge adm-badge--${user.verified ? 'green' : 'yellow'}`}>
                                            {user.verified ? 'Áno' : 'Nie'}
                                        </span>
                                    } />
                                    <Row label="Blokovaný" value={
                                        <span className={`adm-badge adm-badge--${user.blocked ? 'red' : 'gray'}`}>
                                            {user.blocked ? 'Áno' : 'Nie'}
                                        </span>
                                    } />
                                    <Row label="Počet subjektov" value={user.tenant?.length ?? 0} />
                                </Section>

                                {editing ? (
                                    <EditForm
                                        user={user}
                                        onSave={handleSave}
                                        onCancel={() => setEditing(false)}
                                    />
                                ) : (
                                    <div className="adm-drawer-sidebar-footer">
                                        <button
                                            className="adm-block-btn"
                                            onClick={() => setEditing(true)}
                                        >
                                            Upraviť používateľa
                                        </button>
                                    </div>
                                )}
                            </aside>

                            <div className="adm-drawer-content">
                                <div className="adm-tabs">
                                    <button className="adm-tab adm-tab--active">Subjekty</button>
                                </div>

                                <div className="adm-tab-body">
                                    {!user.tenant?.length ? (
                                        <p className="adm-detail-empty">Používateľ nie je členom žiadneho subjektu.</p>
                                    ) : (
                                        <div className="adm-member-list">
                                            {user.tenant.map(t => (
                                                <div
                                                    key={t._id}
                                                    className={`adm-member-row${onTenantClick ? ' adm-row--clickable' : ''}`}
                                                    onClick={onTenantClick ? () => onTenantClick(t._id) : undefined}
                                                >
                                                    <div className="adm-member-avatar" style={{ fontSize: 12 }}>
                                                        {t.companyName?.[0] ?? '?'}
                                                    </div>
                                                    <div className="adm-member-info">
                                                        <span className="adm-member-name">{t.companyName}</span>
                                                        <span className="adm-member-email">
                                                            {t.email}
                                                            {t.identificationNumber && (
                                                                <span style={{ marginLeft: 8, opacity: 0.6 }}>IČO: {t.identificationNumber}</span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    <span className="adm-badge adm-badge--gray" style={{ flexShrink: 0 }}>
                                                        {TYPE_LABELS[t.type] ?? t.type}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}
